import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader } from "../components/ui/Loader";
import {
  NOTIFICATION_META,
  activityFromProjects,
  fetchNotifications,
  markAllNotificationsRead,
} from "../lib/notifications";
import { fetchAllProjects } from "../lib/admin";
import { AppNotification } from "../models/notification";
import { formatRelativeDate } from "../lib/projects";
import { useAuth } from "../lib/auth";

export const Notifications = () => {
  const { profile } = useAuth();
  const isStaff = profile?.role === "admin" || profile?.role === "manager";
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile?.role) return;

    if (isStaff) {
      fetchAllProjects()
        .then((projects) =>
          setNotifications(activityFromProjects(projects, projects.length, "/admin/projects"))
        )
        .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
        .finally(() => setLoading(false));
      return;
    }

    fetchNotifications(null)
      .then((n) => {
        setNotifications(n);
        if (n.some((x) => x.read_at === null)) {
          markAllNotificationsRead().catch(() => {});
          setNotifications((ns) =>
            ns.map((x) => ({ ...x, read_at: x.read_at ?? new Date().toISOString() }))
          );
          window.dispatchEvent(new Event("notifications:read-all"));
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, [isStaff, profile?.role]);

  return (
    <main className="flex-1 px-4 py-5 sm:px-5 sm:py-6 md:px-8 md:py-8">
      {loading ? (
        <Loader />
      ) : error ? (
        <p className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </p>
      ) : (
        <section className="card mx-auto max-w-3xl p-4 sm:p-6">
          <div className="mb-5">
            <h1 className="text-xl font-bold text-white">Сповіщення</h1>
          </div>
          {notifications.length === 0 ? (
            <p className="text-sm text-neutral-400">Сповіщень ще немає</p>
          ) : (
            <ul>
              {notifications.map((item, idx) => {
                const meta = NOTIFICATION_META[item.type];
                const Icon = meta.icon;
                const content = (
                  <>
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${meta.tint}`}
                    >
                      <Icon className="text-xl" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm sm:text-base font-semibold text-white">
                          {item.title}
                        </span>
                        <span className="shrink-0 text-xs sm:text-sm text-neutral-500">
                          {formatRelativeDate(item.created_at)}
                        </span>
                      </span>
                      {item.body && (
                        <span className="mt-0.5 block whitespace-pre-wrap text-xs sm:text-sm text-neutral-400">
                          {item.body}
                        </span>
                      )}

                    </span>
                  </>
                );
                return (
                  <li
                    key={`${item.type}-${item.id}`}
                    className={`py-4${idx > 0 ? " border-t border-line" : " pt-0"}${idx === notifications.length - 1 ? " pb-0" : ""}`}
                  >
                    {item.link ? (
                      <Link
                        to={item.link}
                        className="flex items-start gap-3 hover:opacity-90"
                      >
                        {content}
                      </Link>
                    ) : (
                      <div className="flex items-start gap-3">{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </main>
  );
};
